import { css, html, LitElement, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import type { Commerce, CommerceContext, CommerceEventUnsubscribe } from "@teqtank/commerce-sdk";

export interface TeqtankMarketChangedDetail {
    readonly context: CommerceContext;
    readonly cartCleared: boolean;
}

interface PendingMarket {
    readonly countryCode: string;
    readonly currencyCode: string;
}

@customElement("teqtank-market-selector")
export class TeqtankMarketSelector extends LitElement {
    @property({ attribute: false }) public accessor commerce: Commerce | undefined;
    @property() public accessor label = "Shop country";

    @state() private accessor context: CommerceContext | undefined;
    @state() private accessor selectedCountryCode = "";
    @state() private accessor selectedCurrencyCode = "";
    @state() private accessor pending: PendingMarket | undefined;
    @state() private accessor busy = false;
    @state() private accessor errorMessage = "";

    private boundCommerce: Commerce | undefined;
    private unsubscribe: CommerceEventUnsubscribe | undefined;

    public static override styles = css`
        :host {
            display: inline-flex;
            width: var(--teqtank-market-selector-min-width, 10.5rem);
            max-width: 100%;
            align-items: center;
            color: var(--teqtank-color-text, #16221d);
            font-family: var(--teqtank-font-body, Inter, ui-sans-serif, system-ui, sans-serif);
            container-type: inline-size;
        }

        * {
            box-sizing: border-box;
        }

        .selectors {
            display: flex;
            align-items: center;
            gap: 0.45rem;
        }

        label {
            display: grid;
            gap: 0.2rem;
            color: var(--teqtank-color-text-muted, #69736d);
            font-size: 0.68rem;
            font-weight: 800;
            letter-spacing: 0.04em;
            text-transform: uppercase;
        }

        .selector-label:empty {
            display: none;
        }

        select,
        button {
            min-height: 2.35rem;
            border: 1px solid var(--teqtank-color-border, rgba(22, 34, 29, 0.14));
            border-radius: 999px;
            background: var(
                --teqtank-market-selector-background,
                var(--teqtank-color-surface, #fff)
            );
            color: var(--teqtank-color-text, #16221d);
            font: inherit;
        }

        select {
            width: max-content;
            min-width: var(--teqtank-market-selector-min-width, 10.5rem);
            max-width: 14rem;
            padding: 0.4rem 1.8rem 0.4rem 0.7rem;
        }

        select:disabled {
            opacity: 0.65;
        }

        .confirmation,
        .error {
            position: absolute;
            z-index: 30;
            width: min(22rem, calc(100vw - 2rem));
            margin-top: 0.5rem;
            padding: 0.85rem;
            border: 1px solid var(--teqtank-color-border, rgba(22, 34, 29, 0.14));
            border-radius: 0.8rem;
            background: var(--teqtank-color-surface, #fff);
            box-shadow: var(--teqtank-shadow-medium, 0 1rem 2rem rgba(0, 0, 0, 0.12));
            font-size: 0.82rem;
        }

        .confirmation p,
        .error {
            margin: 0;
        }

        .confirmation div {
            display: flex;
            justify-content: flex-end;
            gap: 0.5rem;
            margin-top: 0.75rem;
        }

        button {
            padding: 0.45rem 0.8rem;
            cursor: pointer;
        }

        button.primary {
            border-color: var(--teqtank-color-accent, #728f4f);
            background: var(--teqtank-color-accent, #728f4f);
            color: var(--teqtank-color-on-accent, #fff);
        }

        .error {
            color: var(--teqtank-color-danger, #9f2d22);
        }

        @container (max-width: 18rem) {
            .selectors {
                align-items: stretch;
                flex-direction: column;
            }
            select {
                width: 100%;
                max-width: none;
            }
        }
    `;

    public override connectedCallback(): void {
        super.connectedCallback();
        this.bindCommerce();
    }

    public override disconnectedCallback(): void {
        this.unsubscribe?.();
        this.unsubscribe = undefined;
        super.disconnectedCallback();
    }

    protected override updated(): void {
        this.bindCommerce();
    }

    protected override render() {
        const context = this.context;
        if (context === undefined) {
            return nothing;
        }

        const country = context.countries.find(
            (candidate) => candidate.code === this.selectedCountryCode,
        );
        const currencies = country?.currencies ?? [];

        return html`
            <div class="selectors" part="selectors" aria-busy=${this.busy ? "true" : "false"}>
                <label part="market-label">
                    <span class="selector-label" part="label">${this.label}</span>
                    <select
                        part="country-select"
                        aria-label=${this.label.trim() || "Shop country"}
                        .value=${this.selectedCountryCode}
                        ?disabled=${this.busy}
                        @change=${this.onCountryChange}
                    >
                        ${context.countries.map(
                            (option) =>
                                html`<option
                                    value=${option.code}
                                    ?selected=${option.code === this.selectedCountryCode}
                                >
                                    ${option.name}
                                </option>`,
                        )}
                    </select>
                </label>
                ${
                    currencies.length > 1
                        ? html`<label part="currency-label">
                              <span part="currency-label-text">Currency</span>
                              <select
                                  part="currency-select"
                                  .value=${this.selectedCurrencyCode}
                                  ?disabled=${this.busy}
                                  @change=${this.onCurrencyChange}
                              >
                                  ${currencies.map(
                                      (currency) =>
                                          html`<option
                                              value=${currency.code}
                                              ?selected=${currency.code === this.selectedCurrencyCode}
                                          >
                                              ${currency.code} (${currency.symbol})
                                          </option>`,
                                  )}
                              </select>
                          </label>`
                        : nothing
                }
            </div>
            ${
                this.pending === undefined
                    ? nothing
                    : html`<div
                          class="confirmation"
                          part="confirmation"
                          role="alertdialog"
                          aria-labelledby="market-change-message"
                          aria-live="assertive"
                          tabindex="-1"
                          @keydown=${this.onConfirmationKeydown}
                      >
                          <p id="market-change-message" part="confirmation-message">
                              Changing shop country will clear the current bag and checkout.
                              Continue?
                          </p>
                          <div part="confirmation-actions">
                              <button
                                  part="secondary-action"
                                  type="button"
                                  @click=${this.cancelPending}
                              >
                                  Cancel
                              </button>
                              <button
                                  part="primary-action"
                                  class="primary"
                                  type="button"
                                  @click=${this.confirmPending}
                              >
                                  Change country
                              </button>
                          </div>
                      </div>`
            }
            ${
                this.errorMessage === ""
                    ? nothing
                    : html`<p class="error" part="error" role="alert">${this.errorMessage}</p>`
            }
        `;
    }

    private bindCommerce(): void {
        if (this.commerce === this.boundCommerce) {
            return;
        }

        this.unsubscribe?.();
        this.boundCommerce = this.commerce;
        this.context = this.commerce?.context;
        this.adoptContext(this.context);
        this.unsubscribe = this.commerce?.market.onChanged((event) => {
            this.context = event.current;
            this.adoptContext(event.current);
        });
    }

    private adoptContext(context: CommerceContext | undefined): void {
        this.selectedCountryCode = context?.market.countryCode ?? "";
        this.selectedCurrencyCode = context?.currency.code ?? "";
        this.pending = undefined;
        this.errorMessage = "";
    }

    private readonly onCountryChange = (event: Event): void => {
        const countryCode = (event.currentTarget as HTMLSelectElement).value;
        const country = this.context?.countries.find((candidate) => candidate.code === countryCode);
        const currencyCode = country?.currencies[0]?.code;
        if (currencyCode === undefined) {
            return;
        }

        this.selectedCountryCode = countryCode;
        this.selectedCurrencyCode = currencyCode;
        void this.switchMarket({ countryCode, currencyCode });
    };

    private readonly onCurrencyChange = (event: Event): void => {
        this.selectedCurrencyCode = (event.currentTarget as HTMLSelectElement).value;
        void this.switchMarket({
            countryCode: this.selectedCountryCode,
            currencyCode: this.selectedCurrencyCode,
        });
    };

    private readonly cancelPending = (): void => {
        this.adoptContext(this.commerce?.context);
        void this.focusCountrySelector();
    };

    private readonly onConfirmationKeydown = (event: KeyboardEvent): void => {
        if (event.key === "Escape") {
            event.preventDefault();
            this.cancelPending();
        }
    };

    private readonly confirmPending = (): void => {
        if (this.pending !== undefined) {
            void this.switchMarket(this.pending, true);
        }
    };

    private async switchMarket(target: PendingMarket, confirmCartClear = false): Promise<void> {
        if (this.commerce === undefined || this.busy) {
            return;
        }

        this.busy = true;
        this.errorMessage = "";
        try {
            const result = await this.commerce.market.switch({
                ...target,
                confirmCartClear,
            });
            if (result.requiresConfirmation) {
                this.pending = target;
                await this.updateComplete;
                this.renderRoot.querySelector<HTMLElement>(".confirmation")?.focus();
                return;
            }

            this.context = result.context;
            this.adoptContext(result.context);
            this.dispatchEvent(
                new CustomEvent<TeqtankMarketChangedDetail>("teqtank-market-changed", {
                    detail: Object.freeze({
                        context: result.context,
                        cartCleared: result.cartCleared,
                    }),
                    bubbles: true,
                    composed: true,
                }),
            );
        } catch (error) {
            this.adoptContext(this.commerce.context);
            this.errorMessage =
                error instanceof Error ? error.message : "The shop country could not be changed.";
        } finally {
            this.busy = false;
        }
    }

    private async focusCountrySelector(): Promise<void> {
        await this.updateComplete;
        this.renderRoot.querySelector<HTMLSelectElement>("select")?.focus();
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "teqtank-market-selector": TeqtankMarketSelector;
    }
}
